import { Injectable } from '@angular/core';
import { ID, QueryEntity } from '@datorama/akita';
import { CartState, CartStore } from './cart.store';
import { combineLatest, map } from 'rxjs';
import { CreativeUnitPackageQuery } from '../ad/ad-unit-package.query';
import { CartItem, CartItemType } from "./cart.model";
import { Project } from '../project';

@Injectable({ providedIn: 'root' })
export class CartQuery extends QueryEntity<CartState> {

	selectItemsFromActivePackage$ = combineLatest([
		this.selectAll(),
		this.creativeUnitPackageQuery.selectActiveId()
	]).pipe(
			// filter(([items, id]) => items.length > 0 && id !== undefined),
			// tap(([items, id]) => console.log('Side Effect', items, id)),
			map(([items, id]) => this.getItemsFromPackage(id))
		);

	selectActiveItemsFromCart$ = this.selectAll({
		filterBy(item: CartItem) {
			const isCorrectType = item.type === CartItemType.VIDEO || item.type === CartItemType.AD_UNIT;

			let hasSetData = true;
			if (item.type === CartItemType.VIDEO) {
                hasSetData = !!(item.itemData?.story?.active || item.metadata?.storyTitle) && !!item.name;
            }

            return isCorrectType && hasSetData;
		}
	})

	constructor(
		protected override store: CartStore,
		protected creativeUnitPackageQuery: CreativeUnitPackageQuery
	) {
		super(store);
	}

	getActiveGroup() {
		return this.getValue().activeGroup;
	}

	getItemsFromPackage(id: string | ID) {
		return this.getAll({
			filterBy: i => i.itemData?.packageId === id
		});
	}

	getItemsFromSamePackageAsActive() {
		const activeItem = this.getActive();
		if (!activeItem) return [];

		return this.getItemsFromSamePackage(activeItem.id);
	}

	getItemsFromSamePackage(packageId: string) {
		const item = this.creativeUnitPackageQuery.getEntity(packageId);
		if (!item) return [];

		return this.getAll({
			filterBy: i => i.itemData?.packageId === item.id
		});
	}

	getItemsFromSameItemDataGroup(groupId?: string) {
		if (!groupId) return [];
		return this.getAll().filter(i => {
			const groupData = i.itemData?.group || i.metadata?.group;

			return i.itemData?.groupId === groupId ||
				groupData?.id === groupId || groupData?.instanceId === groupId;
		});
	}

	getActiveItemsFromCart() {
		return this.getAll({
			filterBy(item: CartItem) {
				const isCorrectType = item.type === CartItemType.VIDEO || item.type === CartItemType.AD_UNIT;

				let hasSetData = true;
				if (item.type === CartItemType.VIDEO) {
					hasSetData = !!item.metadata.storyTitle && !!item.name;
				}

				return isCorrectType && hasSetData;
			}
		})
	}

	/**
	 * Pending items are items that are not yet saved to the server, marked with a temporary ID prefix.
	 * @param projectId
	 * @param pendingIdPrefix
	 */
	getPendingItemsFromCart(projectId: Project['id'], pendingIdPrefix: string = 'temp-') {
		return this.getAll({
			filterBy(item: CartItem) {
				const isProjectMatch = item.projectId === projectId || !projectId;
				const isCorrectType = item.type === CartItemType.AD_UNIT;
				const isPending = item.id?.includes(pendingIdPrefix);

				return isCorrectType && isProjectMatch && isPending;
			}
		})
	}

	getVideoItemThumbnailUrlFromItemData(itemData: any): string | undefined {
		let thumbnailUrl: string | undefined;

		if(itemData?.['UI/story-segment']) {
			const segmentId = itemData?.['UI/story-segment']?.ids?.[0] || 1
			thumbnailUrl = itemData?.['UI/story-segment']?.entities?.[segmentId]?.activeClip?.video?.poster;
		}

		return thumbnailUrl;
	}

	/**
	 * Gets a cart item by ID or sourceId.
	 * Comments store cartItemId which may match either the id or sourceId of a cart item.
	 * @param cartItemId The cart item ID to search for
	 * @returns The matching cart item or undefined
	 */
	getCartItemById(cartItemId: string): CartItem | undefined {
		if (!cartItemId) return undefined;
		return this.getAll().find(ci => ci.id === cartItemId || ci.sourceId === cartItemId);
	}

}
